/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { message } from '@brokerbay/design-components';
import RetsListing from '@bb/domain/dist/types/RetsListing';
import { Button, Checkbox, Collapse, Tooltip } from 'components';
import React, {
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { createOrUpdateFromRetsListing } from '../../../../../api/RetsListingController';
import type { ExtractKeysOfType } from '../../../../../utils/utilTypes';
import TriggerRetsUpdatePageContext from './TriggerRetsListingUpdatePageContext';
import { buildElement, getRowCount, getMongoUpdatesDiff } from '../utils';

const EXCLUDED_KEYS: Partial<Record<keyof RetsListing, true>> = {
  // @ts-ignore
  _id: true,
  createdAt: true,
  updatedAt: true,
};

const NON_PRIMITIVE_KEYS: Record<
  ExtractKeysOfType<RetsListing, any[] | Record<string, string>>,
  true
> = {
  openHouses: true,
  rooms: true,
  // @ts-ignore
  photos: true,
  properties: true,
  mlsType: true,
};

const ListingEditor: React.FC = () => {
  const {
    retsListing,
    updates,
    setUpdates,
    shouldCreateCopy,
    setShouldCreateCopy,
    setRetsListingId,
  } = useContext(TriggerRetsUpdatePageContext);

  /** Used to trigger a full re-render so that uncontrolled inputs update when a new listing is selected */
  const [rootKey, setRootKey] = useState(0);
  const previewUpdateRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setRootKey(prev => prev + 1);
  }, [retsListing]);

  if (!retsListing) return null;

  const submit = async () => {
    const mongoUpdates = getMongoUpdatesDiff(updates, retsListing);

    try {
      const id = await createOrUpdateFromRetsListing({
        shouldCreateCopy,
        // @ts-ignore
        retsListingId: retsListing!._id.toString(),
        updates: mongoUpdates,
      });
      setRetsListingId(id);
      setShouldCreateCopy(false);
      message.success('Submitted Successfully');
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to submit');
    }
  };

  return (
    <Fragment key={rootKey}>
      <Collapse>
        <Collapse.Panel header="Listing Data" key="1">
          <Collapse>
            <Collapse.Panel header="Simple properties" key="1">
              <StyledSimplePropertiesContainer>
                {Object.entries(retsListing).map(
                  ([key, value]: [
                    keyof RetsListing,
                    RetsListing[keyof RetsListing],
                  ]) => {
                    if (
                      EXCLUDED_KEYS[key] ||
                      NON_PRIMITIVE_KEYS[key as keyof typeof NON_PRIMITIVE_KEYS]
                    )
                      return null;
                    return buildElement({
                      key,
                      value: updates[key] ?? value,
                      reactKey: key,
                      setUpdates,
                    });
                  },
                )}
              </StyledSimplePropertiesContainer>
            </Collapse.Panel>
          </Collapse>
          {Object.keys(NON_PRIMITIVE_KEYS).map((key: keyof RetsListing) =>
            buildElement({
              key,
              value: updates[key] ?? retsListing[key],
              setUpdates,
              reactKey: key,
            }),
          )}
        </Collapse.Panel>
      </Collapse>

      <div>
        <Tooltip title="Does not update in real-time">
          <StyledButton
            onClick={() => {
              if (!previewUpdateRef.current) return;
              const transformedUpdates = getMongoUpdatesDiff(
                updates,
                retsListing,
              );
              const newRowsCount = getRowCount(transformedUpdates, 5);
              const currentCount = previewUpdateRef.current.rows || 0;
              previewUpdateRef.current.rows = Math.max(
                currentCount,
                newRowsCount,
              );
              previewUpdateRef.current.value = JSON.stringify(
                transformedUpdates,
                null,
                2,
              );
              previewUpdateRef.current.hidden = false;
            }}
          >
            Preview Updates
          </StyledButton>
        </Tooltip>
        &nbsp;
        <StyledButton
          onClick={() => {
            if (!previewUpdateRef.current) return;
            previewUpdateRef.current.hidden = true;
          }}
        >
          Hide Updates
        </StyledButton>
      </div>

      <textarea readOnly hidden ref={previewUpdateRef} />

      <Checkbox
        checked={shouldCreateCopy}
        onChange={e => setShouldCreateCopy(e.target.checked)}
      >
        <Tooltip title="If unchecked the rets listing will be updated with the new values once submitted, otherwise a copy of the rets listing will be created with the new values">
          Create New Listing
        </Tooltip>
      </Checkbox>

      <StyledButton type="primary" onClick={submit}>
        Submit
      </StyledButton>
    </Fragment>
  );
};

export default ListingEditor;

const StyledButton = styled(Button)`
  width: fit-content;
`;

const StyledSimplePropertiesContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;
